import React, {useRef, useState} from "react";
import {Box, IconButton, Typography, Switch, FormControlLabel, Slider, Avatar} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import SmartButtonIcon from "@mui/icons-material/SmartButton";
import ImageIcon from "@mui/icons-material/Image";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ElementInputWithLabel from "../../patterns/form/elementInputWithLabel";
import CallToActionIcon from "@mui/icons-material/CallToAction";
import {TFunction} from "i18next";
import {TEmailTemplateComponentType, TUrlType} from "../../interfaces/email";
import WyziwygEditor from "../../patterns/wysiwyg/wysiwygEditor";
import {uploadImageFile} from "../../services/OperationService";
import {useTranslation} from "react-i18next";
import UploadFile from "../../patterns/upload/uploadFile";
import {IMAGE_FILE_TYPES} from "../../patterns/dialog/downloadFileProps";
import {TParticipantAttribute} from "../../interfaces/participantAttribute";
import OperationColorPicker from "../operationShowcase/operationColorPicker";
import {ColorResult} from "react-color";

export type LocalComponent = {
  localId: string;
  id?: string;
  order: number;
  type: TEmailTemplateComponentType;
  contenu?: string | null;
  label?: string | null;
  url?: string | null;
  alt?: string | null;
  img?: string | null;
  urlType?: TUrlType | null;
};

type EmailTemplateComponentBlockProps = {
  component: LocalComponent;
  index: number;
  total: number;
  expanded: boolean;
  shopUrl?: string;
  participantAttributes: TParticipantAttribute[];
  operationId: string;
  operationLogoUrl?: string;
  primaryColor?: string;
  onToggle: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onChange: (patch: Partial<LocalComponent>) => void;
  addError: (error: string) => void;
};

export const TYPE_ICONS: Record<TEmailTemplateComponentType, JSX.Element> = {
  [TEmailTemplateComponentType.TEXT]: <TextFieldsIcon fontSize="small" />,
  [TEmailTemplateComponentType.BUTTON]: <SmartButtonIcon fontSize="small" />,
  [TEmailTemplateComponentType.BANNER]: <ImageIcon fontSize="small" />,
  [TEmailTemplateComponentType.HEADER]: <CallToActionIcon fontSize="small" sx={{transform: 'rotate(180deg)'}} />,
  [TEmailTemplateComponentType.FOOTER]: <CallToActionIcon fontSize="small" />,
};

export const INSERTABLE_TYPES: TEmailTemplateComponentType[] = [
  TEmailTemplateComponentType.TEXT,
  TEmailTemplateComponentType.BUTTON,
  TEmailTemplateComponentType.BANNER,
  TEmailTemplateComponentType.HEADER,
  TEmailTemplateComponentType.FOOTER,
];

const stripHtml = (html: string): string =>
  html.replace(/<[^>]*>/g, " ").replace(/&nbsp;/gi, " ").replace(/\s+/g, " ").trim();

const getExcerpt = (c: LocalComponent, t: TFunction): string => {
  switch (c.type) {
  case TEmailTemplateComponentType.TEXT:
    return stripHtml(c.contenu ?? "") || t("email_templates.components.excerpt.empty_text");
  case TEmailTemplateComponentType.BUTTON:
    return c.label
      ? `${c.label}${c.url ? ` → ${c.url}` : ""}`
      : t("email_templates.components.excerpt.button_no_label");
  case TEmailTemplateComponentType.BANNER:
    return c.img ? (c.img.split("/").pop() || c.img) : t("email_templates.components.excerpt.no_image");
  case TEmailTemplateComponentType.FOOTER:
    return c.contenu ? stripHtml(c.contenu) : t("email_templates.components.excerpt.footer");
  case TEmailTemplateComponentType.HEADER: {
    const config = JSON.parse(c.contenu ?? '{}');
    const rawLogoUrl = config.logoUrl;
    const hasCustomLogo = rawLogoUrl && rawLogoUrl !== 'templateemail/headerJoozy.png';
    return hasCustomLogo ? `Logo: ${rawLogoUrl.split('/').pop()}` : t("email_templates.components.excerpt.header_default");
  }
  default:
    return "";
  }
};

const cleanLabel = (label: string): string =>
  label.replace(/[^a-zA-Z0-9]/g, "_").toLowerCase();

export function buildVariableMenus(
  attributes: TParticipantAttribute[],
  editorRef: React.MutableRefObject<any>,
  t: TFunction
): { name: string; label: string; items: { name: string; label: string; action: () => void }[] }[] {
  return [{
    name: "variables",
    label: t("operations.mailing.customisation.wysiwyg.title"),
    items: [
      {name: "firstName", label: t("operations.mailing.customisation.wysiwyg.participant_firstname"), action: (): void => editorRef.current?.insertContent("{{user.firstName}}")},
      {name: "lastName", label: t("operations.mailing.customisation.wysiwyg.participant_lastname"), action: (): void => editorRef.current?.insertContent("{{user.lastName}}")},
      {name: "civility", label: t("operations.mailing.customisation.wysiwyg.civility"), action: (): void => editorRef.current?.insertContent("{{user.civility}}")},
      {name: "points", label: t("operations.mailing.customisation.wysiwyg.points"), action: (): void => editorRef.current?.insertContent("{{user.points}}")},
      {name: "operationName", label: t("operations.mailing.customisation.wysiwyg.operation_name"), action: (): void => editorRef.current?.insertContent("{{operationName}}")},
      {name: "companyName", label: t("operations.mailing.customisation.wysiwyg.company_name"), action: (): void => editorRef.current?.insertContent("{{companyName}}")},
      ...(attributes ?? []).map((attr) => ({
        name: `attr-${attr.id}`,
        label: attr.label,
        action: (): void => editorRef.current?.insertContent(`{{user.attributes.${cleanLabel(attr.label)}}}`),
      })),
    ],
  }];
}


type TextBlockEditorProps = {
  operationId: string;
  contenu: string | null;
  participantAttributes: TParticipantAttribute[];
  primaryColor?: string;
  addError: (error: string) => void;
  onChange: (content: string) => void;
};

function TextBlockEditor(props: TextBlockEditorProps): JSX.Element {
  const {operationId, contenu, participantAttributes, primaryColor, addError, onChange} = props;
  const {t} = useTranslation();
  const initialValueRef = useRef(contenu);
  const editorRef = useRef<any>(null);
  return (
    <WyziwygEditor
      operationId={operationId}
      editorRef={editorRef}
      initialValue={initialValueRef.current}
      setError={addError}
      height={250}
      customMenus={buildVariableMenus(participantAttributes, editorRef, t)}
      primaryColor={primaryColor}
      bodyTextAlign="center"
      onChange={onChange}
    />
  );
}

export default function EmailTemplateComponentBlock(props: EmailTemplateComponentBlockProps): JSX.Element {
  const ASSET_BASE = (process.env.REACT_APP_API_HOST ?? '').replace(/\/?$/, '/');
  const DEFAULT_LIBERTY_LOGO = `${ASSET_BASE}logoJoozyBlack.png`;
  const resolveAssetUrl = (path: string): string =>
    /^https?:\/\//.test(path) ? path : `${ASSET_BASE}${path}`;
  const opacityDefault = 0.3;

  const {
    component, index, total, expanded, participantAttributes, operationId, shopUrl, operationLogoUrl, primaryColor,
    onToggle, onMoveUp, onMoveDown, onDelete, onDuplicate, onChange, addError,
  } = props;

  const isDefaultLibertyLogo = (url: string): boolean =>
    url === 'templateemail/headerJoozy.png' || url === `${ASSET_BASE}templateemail/headerJoozy.png`;
  const {t} = useTranslation();
  const [bgColorPickerOpen, setBgColorPickerOpen] = useState(false);
  const [gradientPickerOpen, setGradientPickerOpen] = useState(false);
  const headerBgColor = ((): string => { try { return JSON.parse(component.contenu ?? '{}').backgroundColor || primaryColor || '#6725D2'; } catch { return primaryColor ?? '#6725D2'; } })();
  const headerOpacity = ((): number => { try { return JSON.parse(component.contenu ?? '{}').opacity ?? opacityDefault; } catch { return opacityDefault; } })();
  const headerGradientColor = ((): string => { try { return JSON.parse(component.contenu ?? '{}').gradientEndColor || '#FFFFFF'; } catch { return '#FFFFFF'; } })();
  const hasCustomHeaderBgColor = ((): boolean => { try { return !!JSON.parse(component.contenu ?? '{}').backgroundColor; } catch { return false; } })();
  const hasCustomHeaderGradientColor = ((): boolean => { try { return !!JSON.parse(component.contenu ?? '{}').gradientEndColor; } catch { return false; } })();
  const hasCustomHeaderOpacity = ((): boolean => { try { return JSON.parse(component.contenu ?? '{}').opacity != null; } catch { return false; } })();
  const [buttonColorPickerOpen, setButtonColorPickerOpen] = useState(false);
  const buttonBgColor = ((): string => { try { return JSON.parse(component.contenu ?? '{}').backgroundColor || primaryColor || '#6725D2'; } catch { return primaryColor ?? '#6725D2'; } })();
  
  const updateConfig = (patch: Record<string, string | undefined>): void => {
    const config = JSON.parse(component.contenu ?? '{}');
    Object.assign(config, patch);
    onChange({contenu: JSON.stringify(config)});
  };

  const handleUploadImage = (file: File): Promise<void> =>
    uploadImageFile(operationId, file)
      .then(res => onChange({img: res.url}))
      .catch(() => addError(t("email_templates.components.errors.upload")));

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        border: expanded ? "2px solid" : "1px solid",
        borderColor: expanded ? "primary.main" : "ornament.dark",
        borderRadius: 1,
        mb: 2,
        backgroundColor: "white",
      }}
    >
      <Box sx={{display: "flex", alignItems: "center"}}>
        <Box sx={{display: "flex", flexDirection: "column", ml: 2}}>
          <IconButton size="small" onClick={onMoveUp} disabled={index === 0}>
            <ExpandLessIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={onMoveDown} disabled={index === total - 1}>
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box
          onClick={onToggle}
          sx={{flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 1, px: 2, py: 1, cursor: "pointer"}}
        >
          <Box sx={{flex: 1, minWidth: 0}}>
            <Typography variant="body2" sx={{fontWeight: "bold", lineHeight: 2}}>
              {t(`email_templates.components.types.${component.type}`)}
            </Typography>
            {!expanded && (
              <Typography variant="caption" color="text.secondary" noWrap sx={{display: "block", lineHeight: 2}}>
                {getExcerpt(component, t)}
              </Typography>
            )}
          </Box>
          <IconButton size="small" onClick={(e): void => { e.stopPropagation(); onDuplicate(); }}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={(e): void => { e.stopPropagation(); onDelete(); }}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>

          <ExpandMoreIcon
            sx={{
              color: "text.secondary",
              transform: expanded ? "rotate(180deg)" : "none",
              transition: "transform 200ms",
            }}
          />
        </Box>
      </Box>

      {expanded && (
        <Box sx={{p: 2, borderTop: "1px solid", borderColor: "ornament.dark"}}>
          {component.type === TEmailTemplateComponentType.TEXT && (
            <TextBlockEditor
              key={component.order}
              operationId={operationId}
              contenu={component.contenu ?? null}
              participantAttributes={participantAttributes}
              primaryColor={primaryColor}
              addError={addError}
              onChange={(content): void => onChange({contenu: content})}
            />
          )}

          {component.type === TEmailTemplateComponentType.BUTTON && (
            <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
              <ElementInputWithLabel
                name={`label-${component.localId}`}
                label={t("email_templates.components.fields.button_label")}
                value={component.label ?? ""}
                placeholder={t("email_templates.components.fields.button_label_placeholder")}
                violations={[]}
                updateValue={(value): void => onChange({label: value})}
                sx={{mb: 0}}
              />
              <Box>
                <Box sx={{display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: 1, gap: 1}}>
                  <Typography variant="caption" sx={{fontWeight: "bold", color: "neutral.main", lineHeight: 1}}>
                    {t("email_templates.components.fields.button_color")}
                  </Typography>
                  {((): JSX.Element | null => {
                    let hasCustomColor = false;
                    try { hasCustomColor = !!JSON.parse(component.contenu ?? '{}').backgroundColor; } catch { /* ignore */ }
                    return hasCustomColor ? (
                      <Typography
                        variant="caption"
                        onClick={(): void => updateConfig({backgroundColor: ''})}
                        sx={{color: "info.main", fontWeight: "bold", fontSize: 9, cursor: "pointer", whiteSpace: "nowrap"}}
                      >
                        {t("email_templates.components.fields.button_color_reset")}
                      </Typography>
                    ) : null;
                  })()}
                </Box>
                {buttonColorPickerOpen && (
                  <OperationColorPicker
                    anchorId={`button-color-picker-${component.localId}`}
                    color={buttonBgColor}
                    changeColor={(c: ColorResult): void => updateConfig({backgroundColor: c.hex})}
                    showChangeColor={buttonColorPickerOpen}
                    setShowChangeColor={setButtonColorPickerOpen}
                  />
                )}
                <Box
                  id={`button-color-picker-${component.localId}`}
                  onClick={(): void => setButtonColorPickerOpen(true)}
                  sx={{
                    display: "flex", alignItems: "center", p: "6px", backgroundColor: "ornament.main",
                    borderRadius: "4px", cursor: "pointer",
                  }}
                >
                  <Avatar variant="square" sx={{width: 22, height: 22, mr: 1, backgroundColor: buttonBgColor}}> </Avatar>
                  <Typography variant="body2" noWrap sx={{fontWeight: "medium"}}>
                    {buttonBgColor}
                  </Typography>
                </Box>
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={component.urlType === "shop"}
                    onChange={(e): void => onChange({urlType: e.target.checked ? "shop" : "manual"})}
                  />
                }
                label={t("email_templates.components.fields.link_type_shop")}
                componentsProps={{typography: {variant: "body2"}}}
              />
              <ElementInputWithLabel
                name={`url-${component.localId}`}
                label={t("email_templates.components.fields.url")}
                value={component.urlType === "shop" ? (shopUrl ?? "") : (component.url ?? "")}
                placeholder={t("email_templates.components.fields.url_placeholder")}
                violations={[]}
                updateValue={(value): void => onChange({url: value})}
                disabled={component.urlType === "shop"}
                sx={{mb: 0}}
              />
            </Box>
          )}

          {component.type === TEmailTemplateComponentType.BANNER && (
            <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
              <Typography variant="body2" color="neutral.main" sx={{fontWeight: "bold"}}>
                {t("email_templates.components.fields.banner_title")}
              </Typography>
              <Typography variant="body2" color="neutral.main">
                {t("email_templates.components.fields.banner_description")}
              </Typography>
              <UploadFile
                action={handleUploadImage}
                setError={addError}
                isPdf={false}
                types={IMAGE_FILE_TYPES}
              />
              {component.img && (
                <Box component="img" src={resolveAssetUrl(component.img)} alt={component.alt ?? ""} sx={{maxWidth: "100%", maxHeight: 150, objectFit: "contain"}} />
              )}
              <ElementInputWithLabel
                name={`alt-${component.localId}`}
                label={t("email_templates.components.fields.alt")}
                value={component.alt ?? ""}
                violations={[]}
                updateValue={(value): void => onChange({alt: value})}
                sx={{mb: 0}}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={component.urlType === "shop"}
                    onChange={(e): void => onChange({urlType: e.target.checked ? "shop" : "manual"})}
                  />
                }
                label={t("email_templates.components.fields.link_type_shop")}
                componentsProps={{typography: {variant: "body2"}}}
              />
              <ElementInputWithLabel
                name={`banner-url-${component.localId}`}
                label={t("email_templates.components.fields.banner_link")}
                value={component.urlType === "shop" ? (shopUrl ?? "") : (component.url ?? "")}
                placeholder={t("email_templates.components.fields.url_placeholder")}
                violations={[]}
                updateValue={(value): void => onChange({url: value})}
                disabled={component.urlType === "shop"}
                sx={{mb: 0}}
              />
            </Box>
          )}

          {component.type === TEmailTemplateComponentType.FOOTER && (
            <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
              <Typography variant="body2" color="neutral.main">
                {t("email_templates.components.fields.footer_info")}
              </Typography>
              <Box sx={{p: 2, borderRadius: 1, backgroundColor: "ornament.main", border: "1px solid", borderColor: "ornament.dark"}}>
                <Typography variant="caption" color="neutral.main" sx={{display: "block", mb: 1}}>
                  {t("email_templates.components.fields.footer_sentence")}
                </Typography>
                <Typography variant="body2" color="neutral.dark">
                  {component.contenu ?? t("email_templates.components.fields.footer_sentence_placeholder")}
                </Typography>
              </Box>
            </Box>
          )}

          {component.type === TEmailTemplateComponentType.HEADER && (
            <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
              <Typography variant="body2" color="neutral.main" sx={{fontWeight: "bold"}}>
                {t("email_templates.components.fields.header_logo")}
              </Typography>
              <UploadFile
                action={(file: File): Promise<void> => {
                  const config = JSON.parse(component.contenu ?? '{}');
                  return uploadImageFile(operationId, file)
                    .then(res => {
                      config.logoUrl = res.url;
                      onChange({contenu: JSON.stringify(config)});
                    })
                    .catch(() => addError(t("email_templates.components.errors.upload")));
                }}
                setError={addError}
                isPdf={false}
                types={IMAGE_FILE_TYPES}
              />
              {((): JSX.Element => {
                const config = JSON.parse(component.contenu ?? '{}');
                const rawLogoUrl = config.logoUrl;
                const hasCustomLogo = rawLogoUrl && !isDefaultLibertyLogo(rawLogoUrl);
                const displaySrc = hasCustomLogo ? rawLogoUrl : (operationLogoUrl || DEFAULT_LIBERTY_LOGO);
                const isRemovable = !!hasCustomLogo;
                return (
                  <Box sx={{textAlign: "center"}}>
                    <Box sx={{position: "relative", display: "inline-block"}}>
                      <Box component="img" src={displaySrc} alt="Logo" sx={{maxWidth: "100%", maxHeight: 150, objectFit: "contain", display: "block"}} />
                      {isRemovable && (
                        <IconButton
                          size="small"
                          onClick={(): void => {
                            const newConfig = {...config, logoUrl: undefined};
                            onChange({contenu: JSON.stringify(newConfig)});
                          }}
                          sx={{
                            position: "absolute", top: -8, right: -8,
                            backgroundColor: "error.main",
                            color: "white", "&:hover": {backgroundColor: "error.dark"},
                            width: 24, height: 24,
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                    {!hasCustomLogo && operationLogoUrl && (
                      <Typography variant="caption" sx={{color: "text.secondary", mt: "-20px", display: "block"}}>
                        {t("email_templates.components.fields.header_logo_operation")}
                      </Typography>
                    )}
                  </Box>
                );
              })()}
              <Box>
                <Box sx={{display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: 1, gap: 1}}>
                  <Typography variant="caption" sx={{fontWeight: "bold", color: "neutral.main", lineHeight: 1}}>
                    {t("email_templates.components.fields.header_bg_color")}
                  </Typography>
                  {hasCustomHeaderBgColor && (
                    <Typography
                      variant="caption"
                      onClick={(): void => updateConfig({backgroundColor: ''})}
                      sx={{color: "info.main", fontWeight: "bold", fontSize: 9, cursor: "pointer", whiteSpace: "nowrap"}}
                    >
                      {t("email_templates.components.fields.header_reset")}
                    </Typography>
                  )}
                </Box>
                {bgColorPickerOpen && (
                  <OperationColorPicker
                    anchorId={`header-bg-color-picker-${component.localId}`}
                    color={headerBgColor}
                    changeColor={(c: ColorResult): void => updateConfig({backgroundColor: c.hex})}
                    showChangeColor={bgColorPickerOpen}
                    setShowChangeColor={setBgColorPickerOpen}
                  />
                )}
                <Box
                  id={`header-bg-color-picker-${component.localId}`}
                  onClick={(): void => setBgColorPickerOpen(true)}
                  sx={{
                    display: "flex", alignItems: "center", p: "6px", backgroundColor: "ornament.main",
                    borderRadius: "4px", cursor: "pointer",
                  }}
                >
                  <Avatar variant="square" sx={{width: 22, height: 22, mr: 1, backgroundColor: headerBgColor}}> </Avatar>
                  <Typography variant="body2" noWrap sx={{fontWeight: "medium"}}>
                    {headerBgColor}
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Box sx={{display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: 1, gap: 1}}>
                  <Typography variant="caption" sx={{fontWeight: "bold", color: "neutral.main", lineHeight: 1}}>
                    {t("email_templates.components.fields.header_gradient_color")}
                  </Typography>
                  {hasCustomHeaderGradientColor && (
                    <Typography
                      variant="caption"
                      onClick={(): void => updateConfig({gradientEndColor: ''})}
                      sx={{color: "info.main", fontWeight: "bold", fontSize: 9, cursor: "pointer", whiteSpace: "nowrap"}}
                    >
                      {t("email_templates.components.fields.header_reset")}
                    </Typography>
                  )}
                </Box>
                {gradientPickerOpen && (
                  <OperationColorPicker
                    anchorId={`header-gradient-color-picker-${component.localId}`}
                    color={headerGradientColor}
                    changeColor={(c: ColorResult): void => updateConfig({gradientEndColor: c.hex})}
                    showChangeColor={gradientPickerOpen}
                    setShowChangeColor={setGradientPickerOpen}
                  />
                )}
                <Box
                  id={`header-gradient-color-picker-${component.localId}`}
                  onClick={(): void => setGradientPickerOpen(true)}
                  sx={{
                    display: "flex", alignItems: "center", p: "6px", backgroundColor: "ornament.main",
                    borderRadius: "4px", cursor: "pointer",
                  }}
                >
                  <Avatar variant="square" sx={{width: 22, height: 22, mr: 1, backgroundColor: headerGradientColor}}> </Avatar>
                  <Typography variant="body2" noWrap sx={{fontWeight: "medium"}}>
                    {headerGradientColor}
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Box sx={{display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: 1, gap: 1}}>
                  <Typography variant="caption" sx={{fontWeight: "bold", color: "neutral.main", lineHeight: 1}}>
                    {t("email_templates.components.fields.header_bg_opacity")}
                  </Typography>
                  {hasCustomHeaderOpacity && (
                    <Typography
                      variant="caption"
                      onClick={(): void => updateConfig({opacity: undefined})}
                      sx={{color: "info.main", fontWeight: "bold", fontSize: 9, cursor: "pointer", whiteSpace: "nowrap"}}
                    >
                      {t("email_templates.components.fields.header_reset")}
                    </Typography>
                  )}
                </Box>
                <Box sx={{display: "flex", alignItems: "center", width: "100%"}}>
                  <Slider
                    value={headerOpacity}
                    min={0}
                    max={1}
                    step={0.05}
                    size="medium"
                    onChange={(_, val): void => updateConfig({opacity: String(val)})}
                    sx={{flex: 1}}
                  />
                  <Typography variant="caption" sx={{fontSize: 12, color: "text.secondary", width: 40, textAlign: "right"}}>
                    {Math.round(headerOpacity * 100)}%
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
